/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { t } from '../../translations/i18n';
import { store } from '../../store/store';
import { useSelector } from 'react-redux';
import GeoJSONReader from 'jsts/org/locationtech/jts/io/GeoJSONReader.js'
import GeoJSONWriter from 'jsts/org/locationtech/jts/io/GeoJSONWriter.js'
import GeometryFactory from 'jsts/org/locationtech/jts/geom/GeometryFactory.js'
import Polygonizer from 'jsts/org/locationtech/jts/operation/polygonize/Polygonizer'
import UnaryUnionOp from 'jsts/org/locationtech/jts/operation/union/UnaryUnionOp'
import Button from '@mui/material/Button';
import Checkmark from '../../icons/Checkmark.svg?react';
import { MmgOperationContainer } from '../../shared/operations/operation-container';
import { OPERATION_TOOL_IDS } from './operation-constants';
import { EMapToolActionType } from '../../store/actions/MapToolActionType';
import { EDIT_MODE, EDIT_MODE_ADDED, EDIT_MODE_MODIFIED } from '../../store/reducers/EditReducer';
import { buttonActiveStyle } from '../edit-modes/select-for-editing-modes';
import { GEOMETRY_ITEM_TYPES } from '../../models/IGeometries';
import mikeSharedTheme from '../styles/mikeSharedTheme';
import MIKE_COLORS from '../styles/mike-colors';
import MikeVisualizer from '../../MikeVisualizer';
import { Feature, FeatureCollection } from 'geojson';
import { IGlobalState } from '../../store/reducers';
import { v4 as uuid } from 'uuid';
import Polygon from 'jsts/org/locationtech/jts/geom/Polygon';

const OPERATION_INPUT_STYLES = css`
  display: flex;
  align-items: center;
  overflow: hidden;
  padding-left: ${mikeSharedTheme.spacing(2)};
  padding-right: ${mikeSharedTheme.spacing(2)};
`;

const OPERATION_DESCRIPTION_STYLES = css`
  color: ${MIKE_COLORS.DARKGREY_DEFAULT};
  font-size: 12px;
`;

const {
  setDrawVectorLayerGeojson,
  clearDrawnVectorLayerData,
  onDrawnDataUpdated,
  enable2DPolylineDrawingV2,
  disable2DPolylineDrawing,
  getCurrentlyDrawnGeojson
} = MikeVisualizer;

const emptyFeatureCollection: FeatureCollection<any, any> = {
  type: 'FeatureCollection',
  features: [],
};

type ClipOperationProps = {
  disabled?: boolean;
  setOperationDone: (fc: FeatureCollection<any, any>, mixedEditModeSet: boolean) => void;
};

/**
 * @name MmgConnectedSplitOperation
 * @summary Clips currently drawn geojson by additionally drawn clip polygon(s).
 *
 * @param props
 */
export function MmgConnectedSplitOperation(props: ClipOperationProps) {
  const { disabled, setOperationDone } = props;
  const { polylineDrawingEnabled } = useSelector((state: IGlobalState) => state.MapToolReducer);
  const activeOperationToolId = useSelector(
    (state: IGlobalState) => state.GeometryClientOperationReducer.activeOperationToolId,
  );

  const { geometryEditItemType } = useSelector((state: IGlobalState) => state.EditReducer);

  const hint = useMemo(
    () => {
      switch (geometryEditItemType) {
        /* case GEOMETRY_ITEM_TYPES.LINE_STRING:
        case GEOMETRY_ITEM_TYPES.MULTI_LINE_STRING:
          return t('OP_CLIPOPERATIONUIDEFINITION_DESCRIPTION_LINE'); */
        case GEOMETRY_ITEM_TYPES.POLYGON:
        case GEOMETRY_ITEM_TYPES.MULTI_POLYGON:
          return t('OP_SPLITOPERATIONUIDEFINITION_DESCRIPTION_POLYGON');
      }
    },
    [geometryEditItemType],
  );

  const isOpen = useMemo(
    () => {
      return activeOperationToolId === OPERATION_TOOL_IDS.SPLIT;
    },
    [activeOperationToolId],
  );

  const [splittingFC, setSplittingFC] = useState({
    ...emptyFeatureCollection,
  });

  const [selectedFC, setSelectedFC] = useState({
    ...emptyFeatureCollection,
  });

  const clipToggled = (open: boolean) => {
    if (open) {
      store.dispatch({
        type: 'clientOperation/SET_ACTIVE_TOOL',
        operationToolId: OPERATION_TOOL_IDS.SPLIT,
      });
    } else {
      store.dispatch({ type: 'clientOperation/EXIT_ACTIVE_TOOL' });
    }
  };

  const acceptSplit = useCallback(
    async () => {
      clearDrawnVectorLayerData();
      store.dispatch({ type: 'clientOperation/EXIT_ACTIVE_TOOL' });
      
      const splittingFeatures = splittingFC.features.filter((f: Feature<any, any>) => {
        const keys = Object.keys(f.properties);
        return !keys.includes(EDIT_MODE);
      });
   
      const featuresToSplit = selectedFC.features.filter((f: Feature<any, any>) => {
        const keys = Object.keys(f.properties);
        return keys.includes(EDIT_MODE);
      });

      const geometryFactory = new GeometryFactory();              
      const geojsonRader = new GeoJSONReader(geometryFactory);  
      const geojsonWriter = new GeoJSONWriter();      
      const splittingFeature = splittingFeatures[0]
      const jstsSplitGeometry = geojsonRader.read(splittingFeature.geometry);
      const processedFeatures = Array<Feature<any>>()
      featuresToSplit.forEach((feature) => { 
        const jstsGeometry = geojsonRader.read(feature.geometry);         
        if (jstsGeometry.getGeometryType() === GEOMETRY_ITEM_TYPES.POLYGON){
          const polygon = jstsGeometry as Polygon
          if (polygon.getNumInteriorRing() === 0)
          {
            const unionGeometryFactory = new GeometryFactory();  
            const jstsPolygonColl = unionGeometryFactory.createGeometryCollection([jstsGeometry.getExteriorRing(), jstsSplitGeometry]);
            const unaryUnionOp = new UnaryUnionOp(jstsPolygonColl);
            const union = unaryUnionOp.union();
            const polygonizer = new Polygonizer();
            polygonizer.add(union);
            const polygons = polygonizer.getPolygons();
            polygons.array.forEach((polygon, index: number) => {  
              const mode = index === 0 ? EDIT_MODE_MODIFIED : EDIT_MODE_ADDED
              const newId = uuid()
              const newProperties = index === 0 ? {...feature.properties, [EDIT_MODE]: mode} : {id: newId, [EDIT_MODE]: mode}
              const split = geojsonWriter.write(polygon)          
              const newFeature = {...feature, geometry: split, id: newId, properties: newProperties}
              processedFeatures.push(newFeature)
            })     
          }          
        }    
      }); 
 
      const result = {
        ...selectedFC,
        features: processedFeatures        
      } 
      setDrawVectorLayerGeojson(result as FeatureCollection<any,any>);
      setOperationDone(result as FeatureCollection<any,any>,true);
    },
    [splittingFC, selectedFC, setOperationDone],
  );

  useEffect(
    () => {
      if (isOpen) {
        setSelectedFC(getCurrentlyDrawnGeojson());
        enable2DPolylineDrawingV2({singleItem: true});
        store.dispatch({ type: EMapToolActionType.ENABLE_POLYLINE_DRAWING });
      }
      else{
        setSplittingFC(emptyFeatureCollection)
        setSelectedFC(emptyFeatureCollection);
      }
    },
    [isOpen],
  );


  useEffect(
    () => {
      const drawUpdateCallback = (updatedFC: FeatureCollection<any, any>) => {
        if (isOpen && polylineDrawingEnabled) {
          setSplittingFC(updatedFC);
        }
      };

      const unsubscribers = [];
      const unsubscribeOnDrawnDataUpdated = onDrawnDataUpdated(drawUpdateCallback);
      unsubscribers.push(unsubscribeOnDrawnDataUpdated);

      return () => {
        unsubscribers.forEach((unsubscribe) => unsubscribe());
        disable2DPolylineDrawing();
      };
    },
    [polylineDrawingEnabled, isOpen],
  );

  return (
    <MmgOperationContainer
      isOpen={isOpen}
      onContainerToggled={clipToggled}
      operationName={t('UI_SPLITOPERATION')}
      disabled={disabled}
    >
      <div css={OPERATION_INPUT_STYLES}>
        <div css={OPERATION_DESCRIPTION_STYLES}>{t('SPLIT_DRAW_TOOLS_TITLE') + " " + hint}</div>
        <Button onClick={acceptSplit} css={buttonActiveStyle}>
          <Checkmark />
        </Button>      
      </div>
    </MmgOperationContainer>
  );
}
